/**
 * Jotai Atoms for Submission List State Management
 *
 * Following Jotai best practices:
 * - atomWithStorage for persistent state (localStorage)
 * - Primitive atoms for simple state
 * - Derived atoms for computed values
 *
 * @see https://jotai.org/docs/utilities/storage
 * @see https://jotai.org/docs/guides/composing-atoms
 */

import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type {
  BatchDialogState,
  BatchOperationState,
  DownloadProgress,
  PaginationState,
  ReportStatusFilter,
} from "./types";

// ============================================================================
// Persistent Atoms (localStorage)
// ============================================================================

/**
 * Selected form ID - persisted across sessions
 */
export const selectedFormAtom = atomWithStorage<string>(
  "submissions-selected-form",
  ""
);

/**
 * Page size preference - persisted across sessions
 * Default: 25 for better UX with cursor pagination
 */
export const pageSizeAtom = atomWithStorage<number>(
  "submissions-page-size",
  25
);

// ============================================================================
// Launch Filter Atoms
// ============================================================================

/**
 * Selected launch ID for filtering submissions
 */
export const selectedLaunchIdAtom = atom<string>("");

// ============================================================================
// Report Status Filter Atom
// ============================================================================

/**
 * Filter by report generation status
 */
export const reportStatusFilterAtom = atom<ReportStatusFilter>("all");

// ============================================================================
// Batch Selection Atoms
// ============================================================================

/**
 * Set of selected submission IDs for batch operations
 * Using Set for O(1) lookup performance
 */
export const selectedIdsAtom = atom<Set<number>>(new Set());

/**
 * Derived atom: count of selected items
 */
export const selectedCountAtom = atom((get) => get(selectedIdsAtom).size);

/**
 * Derived atom: check if any items are selected
 */
export const hasSelectionAtom = atom((get) => get(selectedIdsAtom).size > 0);

// ============================================================================
// Batch Dialog Atoms
// ============================================================================

/**
 * Batch dialog state (which dialog is open, template selection, notify option)
 */
export const batchDialogAtom = atom<BatchDialogState>({
  open: null,
  templateId: "",
  notify: false,
});

// ============================================================================
// Batch Operation State Atoms
// ============================================================================

/**
 * Download progress state
 */
export const downloadProgressAtom = atom<DownloadProgress>({
  current: 0,
  total: 0,
});

/**
 * Batch operation loading states
 */
export const batchOperationStateAtom = atom<BatchOperationState>({
  isDownloading: false,
  isSendingEmails: false,
  isGenerating: false,
  downloadProgress: { current: 0, total: 0 },
});

// ============================================================================
// Pagination Atoms
// ============================================================================

/**
 * Current page (for backward compatibility, used in client-side pagination fallback)
 */
export const currentPageAtom = atom<number>(1);

/**
 * Derived pagination state combining page and size
 */
export const paginationAtom = atom<PaginationState>((get) => ({
  currentPage: get(currentPageAtom),
  pageSize: get(pageSizeAtom),
}));

/**
 * Track loaded submissions count (for cursor pagination progress)
 */
export const loadedCountAtom = atom<number>(0);

// ============================================================================
// Action Atoms (Write-only atoms for encapsulated state updates)
// ============================================================================

/**
 * Toggle selection for a single submission
 */
export const toggleSelectionAtom = atom(
  null,
  (get, set, submissionId: number) => {
    const current = get(selectedIdsAtom);
    const next = new Set(current);
    if (next.has(submissionId)) {
      next.delete(submissionId);
    } else {
      next.add(submissionId);
    }
    set(selectedIdsAtom, next);
  }
);

/**
 * Select all submissions on current page
 */
export const selectAllOnPageAtom = atom(
  null,
  (get, set, pageIds: number[]) => {
    const current = get(selectedIdsAtom);
    const allSelected = pageIds.every((id) => current.has(id));

    const next = new Set(current);
    if (allSelected) {
      // Deselect all on page
      pageIds.forEach((id) => next.delete(id));
    } else {
      // Select all on page
      pageIds.forEach((id) => next.add(id));
    }
    set(selectedIdsAtom, next);
  }
);

/**
 * Clear all selections
 */
export const clearSelectionAtom = atom(null, (_get, set) => {
  set(selectedIdsAtom, new Set());
});

/**
 * Reset filters to initial state
 */
export const resetFiltersAtom = atom(null, (_get, set) => {
  set(selectedLaunchIdAtom, "");
  set(reportStatusFilterAtom, "all");
  set(currentPageAtom, 1);
});

/**
 * Open batch dialog
 */
export const openBatchDialogAtom = atom(
  null,
  (_get, set, dialogType: "generate" | "download" | "email") => {
    set(batchDialogAtom, {
      open: dialogType,
      templateId: "",
      notify: false,
    });
  }
);

/**
 * Close batch dialog and reset state
 */
export const closeBatchDialogAtom = atom(null, (_get, set) => {
  set(batchDialogAtom, {
    open: null,
    templateId: "",
    notify: false,
  });
});

/**
 * Update batch dialog template selection
 */
export const setBatchTemplateAtom = atom(
  null,
  (get, set, templateId: string) => {
    const current = get(batchDialogAtom);
    set(batchDialogAtom, { ...current, templateId });
  }
);

/**
 * Update batch dialog notify option
 */
export const setBatchNotifyAtom = atom(
  null,
  (get, set, notify: boolean) => {
    const current = get(batchDialogAtom);
    set(batchDialogAtom, { ...current, notify });
  }
);
